<!-- HomePage.vue -->
<template>
  <div class="min-h-screen bg-black text-white">
    <!-- Navigation -->
    <NavBar />

    <!-- Hero Section with Slider -->
    <section id="home" class="relative overflow-hidden pt-48 lg:pt-32">
      <!-- Animated Background -->
      <div class="absolute inset-0">
        <div
          class="absolute top-0 left-1/4 w-96 h-96 bg-brand/10 rounded-full blur-3xl animate-pulse"
        ></div>
        <div
          class="absolute bottom-0 right-1/4 w-96 h-96 bg-brand-dark/10 rounded-full blur-3xl animate-pulse animation-delay-1000"
        ></div>
      </div>

      <!-- Grid Pattern Overlay -->
      <div class="absolute inset-0 opacity-5 grid-pattern"></div>

      <div class="relative max-w-7xl mx-auto px-6 py-16 lg:py-24">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <!-- Left Content -->
          <div class="space-y-8">
            <!-- Partnership Badge -->
            <div
              class="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-brand/20 to-brand-dark/20 border border-brand/30 rounded-full"
            >
              <svg
                class="w-4 h-4 text-brand"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              <span class="text-sm font-semibold text-brand"
                >Strategic Partnership</span
              >
            </div>

            <!-- Main Headline -->
            <h1 class="text-5xl lg:text-7xl font-bold leading-tight">
              <span class="text-white">Where Two</span>
              <br />
              <span
                class="text-transparent bg-clip-text bg-gradient-to-r from-accent-yellow via-accent-red to-accent-red-dark"
              >
                Visions Unite
              </span>
              <br />
              <span class="text-white">For Excellence</span>
            </h1>

            <p class="text-xl text-gray-400 leading-relaxed max-w-xl">
              A powerful collaboration between two industry-leading real estate
              companies, bringing together unparalleled expertise, innovation,
              and dedication to transform the landscape of premium property
              development.
            </p>

            <!-- CTA Buttons -->
            <div class="flex flex-col sm:flex-row gap-4">
              <button
                class="group relative px-8 py-4 bg-gradient-to-r from-brand to-brand-dark rounded-full text-black font-bold text-lg overflow-hidden hover:shadow-2xl hover:shadow-brand/50 transition-all duration-300"
              >
                <span
                  class="relative z-10 flex items-center justify-center gap-2"
                >
                  Explore Projects
                  <svg
                    class="w-5 h-5 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </span>
              </button>
              <button
                class="px-8 py-4 border-2 border-brand text-brand rounded-full font-bold text-lg hover:bg-brand hover:text-black transition-all duration-300"
              >
                Learn More
              </button>
            </div>

            <!-- Stats -->
            <div class="grid grid-cols-3 gap-6 pt-8">
              <div class="text-center lg:text-left">
                <div
                  class="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-light to-brand-dark"
                >
                  500+
                </div>
                <div class="text-sm text-gray-500 mt-1">Projects Delivered</div>
              </div>
              <div class="text-center lg:text-left">
                <div
                  class="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-light to-brand-dark"
                >
                  2
                </div>
                <div class="text-sm text-gray-500 mt-1">Elite Partners</div>
              </div>
              <div class="text-center lg:text-left">
                <div
                  class="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-light to-brand-dark"
                >
                  6+
                </div>
                <div class="text-sm text-gray-500 mt-1">Years Combined</div>
              </div>
            </div>
          </div>

          <!-- Right Content - Image Slider -->
          <div class="relative">
            <div
              class="relative rounded-3xl overflow-hidden border border-brand/30 bg-gradient-to-br from-gray-900 to-black p-1"
            >
              <!-- Slider Container -->
              <div
                class="relative rounded-2xl overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900 aspect-[4/5]"
              >
                <!-- Slides -->
                <transition-group name="fade" tag="div">
                  <div
                    v-for="(slide, index) in slides"
                    v-show="currentSlide === index"
                    :key="index"
                    class="absolute inset-0"
                  >
                    <img
                      :src="slide.image"
                      :alt="slide.alt"
                      class="w-full h-full object-cover"
                    />

                    <!-- Gradient Overlay -->
                    <div
                      class="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"
                    ></div>

                    <!-- Slide Info Card -->
                    <div
                      class="absolute bottom-6 left-6 right-6 bg-black/80 backdrop-blur-xl border border-brand/30 rounded-2xl p-5"
                    >
                      <div class="flex items-center gap-4">
                        <div
                          class="w-14 h-14 bg-gradient-to-br from-brand to-brand-dark rounded-xl flex items-center justify-center flex-shrink-0"
                        >
                          <svg
                            class="w-7 h-7 text-black"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              stroke-width="2"
                              d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                            />
                          </svg>
                        </div>
                        <div>
                          <div class="font-bold text-white text-lg">
                            {{ slide.title }}
                          </div>
                          <div class="text-sm text-gray-400">
                            {{ slide.subtitle }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </transition-group>

                <!-- Navigation Arrows -->
                <button
                  @click="prevSlide"
                  class="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-brand text-white rounded-full flex items-center justify-center transition-all duration-300 backdrop-blur-sm z-10"
                >
                  <svg
                    class="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
                <button
                  @click="nextSlide"
                  class="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-brand text-white rounded-full flex items-center justify-center transition-all duration-300 backdrop-blur-sm z-10"
                >
                  <svg
                    class="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>

                <!-- Slide Indicators -->
                <div
                  class="absolute bottom-24 left-1/2 -translate-x-1/2 flex gap-2 z-10"
                >
                  <button
                    v-for="(slide, index) in slides"
                    :key="index"
                    @click="currentSlide = index"
                    :class="[
                      'w-2 h-2 rounded-full transition-all duration-300',
                      currentSlide === index
                        ? 'w-8 bg-brand'
                        : 'bg-white/50 hover:bg-white',
                    ]"
                  ></button>
                </div>
              </div>
            </div>

            <!-- Decorative Elements -->
            <div
              class="absolute -top-4 -right-4 w-24 h-24 bg-brand/20 rounded-full blur-2xl"
            ></div>
            <div
              class="absolute -bottom-4 -left-4 w-32 h-32 bg-brand-dark/20 rounded-full blur-2xl"
            ></div>
          </div>
        </div>
      </div>

      <!-- Scroll Indicator -->
      <div class="relative flex justify-center pb-12">
        <div class="animate-bounce">
          <svg
            class="w-8 h-8 text-brand"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 14l-7 7m0 0l-7-7m7 7V3"
            />
          </svg>
        </div>
      </div>
    </section>

    <!-- Projects Section -->
    <section
      id="projects"
      class="relative py-24 px-6 overflow-hidden bg-gradient-to-b from-gray-950 to-black"
    >
      <!-- Background Elements -->
      <div class="absolute inset-0">
        <div
          class="absolute top-1/4 right-0 w-96 h-96 bg-brand/10 rounded-full blur-3xl"
        ></div>
        <div
          class="absolute bottom-1/4 left-0 w-96 h-96 bg-brand-dark/10 rounded-full blur-3xl"
        ></div>
      </div>

      <div class="relative max-w-7xl mx-auto">
        <!-- Section Header -->
        <div class="text-center mb-16">
          <div
            class="inline-flex items-center gap-2 px-4 py-2 bg-brand/10 border border-brand/30 rounded-full mb-6"
          >
            <svg
              class="w-4 h-4 text-brand"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
            <span class="text-sm font-semibold text-brand"
              >Our Projects</span
            >
          </div>
          <h2 class="text-5xl lg:text-6xl font-bold mb-6">
            <span class="text-white">Hutu </span>
            <span
              class="text-transparent bg-clip-text bg-gradient-to-r from-accent-yellow-light via-accent-yellow to-accent-yellow-dark"
              >Resort Estates</span
            >
          </h2>
          <p class="text-xl text-gray-400 max-w-3xl mx-auto">
            A joint venture by MshelHomes Limited and AibenProperties, presenting
            Hutu Prestige, Hutu Exclusive, Hutu Royal, and Hutu Pent Haven —
            four exceptional resort developments with world-class amenities.
          </p>
        </div>

        <!-- Staggered Grid Layout -->
        <div class="grid lg:grid-cols-2 gap-8 lg:gap-10">
          <!-- Project 1 - Hutu Prestige (Top Left) -->
          <div class="group relative lg:mt-0">
            <div
              class="relative bg-gradient-to-br from-gray-900 to-black border border-brand/20 rounded-3xl overflow-hidden hover:border-brand/60 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-brand/20"
            >
              <!-- Image Container -->
              <div class="relative h-80 overflow-hidden">
                <img
                  src="../assets/images/horse-ride.jpg"
                  alt="Hutu Prestige Resort"
                  class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div
                  class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"
                ></div>

                <!-- Company Badge -->
                <div
                  class="absolute top-6 right-6 px-4 py-2 bg-black/80 backdrop-blur-md border border-accent-red/30 rounded-full"
                >
                  <span class="text-sm font-semibold text-accent-red">AibenProperties</span>
                </div>
              </div>

              <!-- Content -->
              <div class="p-8">
                <div class="flex items-start justify-between mb-4">
                  <div>
                    <h3
                      class="text-3xl font-bold text-white mb-2 group-hover:text-brand transition-colors"
                    >
                      Hutu Prestige
                    </h3>
                    <p class="text-brand font-semibold text-lg">
                      The Recreational Resort
                    </p>
                  </div>
                  <div
                    class="w-14 h-14 bg-gradient-to-br from-brand to-brand-dark rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                  >
                    <svg
                      class="w-7 h-7 text-black"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>

                <p class="text-gray-400 leading-relaxed mb-6">
                  An exceptional recreational haven featuring world-class
                  amenities, entertainment facilities, and leisure spaces
                  designed for ultimate relaxation and family bonding.
                </p>

                <div class="flex items-center gap-4 text-sm text-gray-500">
                  <div class="flex items-center gap-2">
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span>Premium Location</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                      />
                    </svg>
                    <span>Luxury Amenities</span>
                  </div>
                </div>

                <router-link
                  to="/projects/hutu-prestige"
                  class="mt-6 flex items-center gap-2 text-brand font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  View Details
                  <svg
                    class="w-4 h-4 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </router-link>
              </div>
            </div>
          </div>

          <!-- Project 2 - Hutu Exclusive (Top Right) -->
          <div class="group relative lg:mt-0">
            <div
              class="relative bg-gradient-to-br from-gray-900 to-black border border-brand/20 rounded-3xl overflow-hidden hover:border-brand/60 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-brand/20"
            >
              <!-- Image Container -->
              <div class="relative h-80 overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?w=800&h=600&fit=crop"
                  alt="Hutu Exclusive Resort"
                  class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div
                  class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"
                ></div>

                <!-- Company Badge -->
                <div
                  class="absolute top-6 right-6 px-4 py-2 bg-black/80 backdrop-blur-md border border-accent-yellow/30 rounded-full"
                >
                  <span class="text-sm font-semibold text-accent-yellow">MshelHomes Limited</span>
                </div>
              </div>

              <!-- Content -->
              <div class="p-8">
                <div class="flex items-start justify-between mb-4">
                  <div>
                    <h3
                      class="text-3xl font-bold text-white mb-2 group-hover:text-brand transition-colors"
                    >
                      Hutu Exclusive
                    </h3>
                    <p class="text-brand font-semibold text-lg">
                      The Golf Resort
                    </p>
                  </div>
                  <div
                    class="w-14 h-14 bg-gradient-to-br from-brand to-brand-dark rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                  >
                    <svg
                      class="w-7 h-7 text-black"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                </div>

                <p class="text-gray-400 leading-relaxed mb-6">
                  A prestigious golf resort featuring championship courses,
                  elegant clubhouses, and refined living spaces for discerning
                  individuals who appreciate excellence.
                </p>

                <div class="flex items-center gap-4 text-sm text-gray-500">
                  <div class="flex items-center gap-2">
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span>Golf Paradise</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                      />
                    </svg>
                    <span>Elite Living</span>
                  </div>
                </div>

                <router-link
                  to="/projects/hutu-exclusive"
                  class="mt-6 flex items-center gap-2 text-brand font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  View Details
                  <svg
                    class="w-4 h-4 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </router-link>
              </div>
            </div>
          </div>

          <!-- Project 3 - Hutu Royal (Bottom Left) -->
          <div class="group relative lg:mt-0">
            <div
              class="relative bg-gradient-to-br from-gray-900 to-black border border-brand/20 rounded-3xl overflow-hidden hover:border-brand/60 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-brand/20"
            >
              <!-- Image Container -->
              <div class="relative h-80 overflow-hidden">
                <img
                  src="../assets/images/aiben1.jpeg"
                  alt="Hutu Royal Polo Resort"
                  class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div
                  class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"
                ></div>

                <!-- Company Badge -->
                <div
                  class="absolute top-6 right-6 px-4 py-2 bg-black/80 backdrop-blur-md border border-accent-red/30 rounded-full"
                >
                  <span class="text-sm font-semibold text-accent-red">AibenProperties</span>
                </div>
              </div>

              <!-- Content -->
              <div class="p-8">
                <div class="flex items-start justify-between mb-4">
                  <div>
                    <h3
                      class="text-3xl font-bold text-white mb-2 group-hover:text-brand transition-colors"
                    >
                      Hutu Royal
                    </h3>
                    <p class="text-brand font-semibold text-lg">
                      The Polo Resort
                    </p>
                  </div>
                  <div
                    class="w-14 h-14 bg-gradient-to-br from-brand to-brand-dark rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                  >
                    <svg
                      class="w-7 h-7 text-black"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                      />
                    </svg>
                  </div>
                </div>

                <p class="text-gray-400 leading-relaxed mb-6">
                  An aristocratic polo resort combining equestrian excellence
                  with luxurious accommodations, offering an exclusive sporting
                  and lifestyle experience for the elite.
                </p>

                <div class="flex items-center gap-4 text-sm text-gray-500">
                  <div class="flex items-center gap-2">
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span>Equestrian Estate</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                      />
                    </svg>
                    <span>Royal Standards</span>
                  </div>
                </div>

                <router-link
                  to="/projects/hutu-royal"
                  class="mt-6 flex items-center gap-2 text-brand font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  View Details
                  <svg
                    class="w-4 h-4 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </router-link>
              </div>
            </div>
          </div>

          <!-- Project 4 - Hutu Pent Haven (Bottom Right) -->
          <div class="group relative lg:mt-0">
            <div
              class="relative bg-gradient-to-br from-gray-900 to-black border border-brand/20 rounded-3xl overflow-hidden hover:border-brand/60 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-brand/20"
            >
              <!-- Image Container -->
              <div class="relative h-80 overflow-hidden">
                <img
                  src="../assets/images/penthouse.jpg"
                  alt="Hutu Pent Haven Resort"
                  class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                />
                <div
                  class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"
                ></div>

                <!-- Company Badge -->
                <div
                  class="absolute top-6 right-6 px-4 py-2 bg-black/80 backdrop-blur-md border border-accent-yellow/30 rounded-full"
                >
                  <span class="text-sm font-semibold text-accent-yellow">MshelHomes Limited</span>
                </div>
              </div>

              <!-- Content -->
              <div class="p-8">
                <div class="flex items-start justify-between mb-4">
                  <div>
                    <h3
                      class="text-3xl font-bold text-white mb-2 group-hover:text-brand transition-colors"
                    >
                      Hutu Pent Haven
                    </h3>
                    <p class="text-brand font-semibold text-lg">
                      The Lake Resort
                    </p>
                  </div>
                  <div
                    class="w-14 h-14 bg-gradient-to-br from-brand to-brand-dark rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform"
                  >
                    <svg
                      class="w-7 h-7 text-black"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                      />
                    </svg>
                  </div>
                </div>

                <p class="text-gray-400 leading-relaxed mb-6">
                  A serene lakeside sanctuary offering breathtaking water views,
                  tranquil surroundings, and premium penthouse residences for
                  those seeking peace and natural beauty.
                </p>

                <div class="flex items-center gap-4 text-sm text-gray-500">
                  <div class="flex items-center gap-2">
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span>Lakefront Living</span>
                  </div>
                  <div class="flex items-center gap-2">
                    <svg
                      class="w-4 h-4"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                      />
                    </svg>
                    <span>Natural Serenity</span>
                  </div>
                </div>

                <router-link
                  to="/projects/hutu-pent-haven"
                  class="mt-6 flex items-center gap-2 text-brand font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  View Details
                  <svg
                    class="w-4 h-4 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </router-link>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>

    <!-- About Section -->
    <section id="about" class="relative py-24 px-6 overflow-hidden bg-black">
      <!-- Background Elements -->
      <div class="absolute inset-0">
        <div
          class="absolute top-0 left-1/3 w-96 h-96 bg-brand/5 rounded-full blur-3xl"
        ></div>
        <div
          class="absolute bottom-0 right-1/3 w-96 h-96 bg-brand-dark/5 rounded-full blur-3xl"
        ></div>
      </div>

      <div class="relative max-w-7xl mx-auto">
        <!-- About Us Subsection -->
        <div class="mb-32">
          <!-- Section Header -->
          <div class="text-center mb-16">
            <div
              class="inline-flex items-center gap-2 px-4 py-2 bg-brand/10 border border-brand/30 rounded-full mb-6"
            >
              <svg
                class="w-4 h-4 text-brand"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <span class="text-sm font-semibold text-brand"
                >About Us</span
              >
            </div>
            <h2 class="text-5xl lg:text-6xl font-bold mb-6">
              <span class="text-white">Building The </span>
              <span
                class="text-transparent bg-clip-text bg-gradient-to-r from-accent-red-light via-accent-red to-accent-red-dark"
                >Future Together</span
              >
            </h2>
          </div>

          <!-- About Content Grid -->
          <div class="grid lg:grid-cols-2 gap-12 items-center">
            <!-- Image Side -->
            <div class="relative order-2 lg:order-1">
              <div
                class="relative rounded-3xl overflow-hidden border border-brand/30 bg-gradient-to-br from-gray-900 to-black p-1"
              >
                <div class="relative rounded-2xl overflow-hidden h-[600px]">
                  <img
                    src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&h=1000&fit=crop"
                    alt="Modern Building Architecture"
                    class="w-full h-full object-cover"
                  />
                  <!-- Gradient Overlay -->
                  <div
                    class="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"
                  ></div>

                  <!-- Stats Overlay Card -->
                  <div
                    class="absolute bottom-8 left-8 right-8 bg-black/90 backdrop-blur-xl border border-brand/30 rounded-2xl p-6"
                  >
                    <div class="grid grid-cols-3 gap-4 text-center">
                      <div>
                        <div
                          class="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-light to-brand-dark"
                        >
                          500+
                        </div>
                        <div class="text-xs text-gray-400 mt-1">Projects</div>
                      </div>
                      <div>
                        <div
                          class="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-light to-brand-dark"
                        >
                          6+
                        </div>
                        <div class="text-xs text-gray-400 mt-1">Years</div>
                      </div>
                      <div>
                        <div
                          class="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-brand-light to-brand-dark"
                        >
                          100%
                        </div>
                        <div class="text-xs text-gray-400 mt-1">Excellence</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Decorative Elements -->
              <div
                class="absolute -top-6 -left-6 w-32 h-32 bg-brand/20 rounded-full blur-2xl"
              ></div>
              <div
                class="absolute -bottom-6 -right-6 w-40 h-40 bg-brand-dark/20 rounded-full blur-2xl"
              ></div>
            </div>

            <!-- Text Content Side -->
            <div class="space-y-6 order-1 lg:order-2">
              <div
                class="inline-flex items-center gap-3 px-5 py-2.5 bg-gradient-to-r from-brand/20 to-brand-dark/20 border border-brand/30 rounded-full"
              >
                <div class="flex -space-x-2">
                  <div
                    class="w-8 h-8 bg-brand rounded-full border-2 border-black flex items-center justify-center text-xs font-bold text-black"
                  >
                    A
                  </div>
                  <div
                    class="w-8 h-8 bg-brand-dark rounded-full border-2 border-black flex items-center justify-center text-xs font-bold text-black"
                  >
                    M
                  </div>
                </div>
                <span class="text-sm font-semibold text-brand"
                  >AibenProperties × MshelHomes Limited</span
                >
              </div>

              <h3 class="text-4xl font-bold text-white leading-tight">
                A Strategic Partnership Built on Excellence and Innovation
              </h3>

              <p class="text-lg text-gray-400 leading-relaxed">
                Our partnership represents the convergence of two
                industry-leading real estate powerhouses, bringing together
                decades of combined expertise, innovation, and unwavering
                commitment to excellence. United by a shared vision to transform
                the landscape of premium property development, we've joined
                forces to deliver exceptional residential and commercial
                projects that set new standards in quality, design, and
                sustainability.
              </p>

              <p class="text-lg text-gray-400 leading-relaxed">
                With AibenProperties' renowned expertise in luxury developments
                and MshelHomes Limited's proven track record in creating
                thriving communities, our collaboration ensures that every
                project benefits from the best of both worlds. We pride
                ourselves on integrity, transparency, and a client-first
                approach that has earned the trust of thousands of satisfied
                homeowners and investors.
              </p>

              <div class="grid sm:grid-cols-2 gap-6 pt-4">
                <!-- Value 1 -->
                <div class="flex gap-4">
                  <div
                    class="w-12 h-12 bg-gradient-to-br from-brand/20 to-brand-dark/20 border border-brand/30 rounded-xl flex items-center justify-center flex-shrink-0"
                  >
                    <svg
                      class="w-6 h-6 text-brand"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-white font-bold mb-1">Quality Assurance</h4>
                    <p class="text-sm text-gray-500">
                      Premium materials and craftsmanship
                    </p>
                  </div>
                </div>

                <!-- Value 2 -->
                <div class="flex gap-4">
                  <div
                    class="w-12 h-12 bg-gradient-to-br from-brand/20 to-brand-dark/20 border border-brand/30 rounded-xl flex items-center justify-center flex-shrink-0"
                  >
                    <svg
                      class="w-6 h-6 text-brand"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M13 10V3L4 14h7v7l9-11h-7z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-white font-bold mb-1">Innovation First</h4>
                    <p class="text-sm text-gray-500">
                      Cutting-edge design and technology
                    </p>
                  </div>
                </div>

                <!-- Value 3 -->
                <div class="flex gap-4">
                  <div
                    class="w-12 h-12 bg-gradient-to-br from-brand/20 to-brand-dark/20 border border-brand/30 rounded-xl flex items-center justify-center flex-shrink-0"
                  >
                    <svg
                      class="w-6 h-6 text-brand"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-white font-bold mb-1">Timely Delivery</h4>
                    <p class="text-sm text-gray-500">
                      On-schedule project completion
                    </p>
                  </div>
                </div>

                <!-- Value 4 -->
                <div class="flex gap-4">
                  <div
                    class="w-12 h-12 bg-gradient-to-br from-brand/20 to-brand-dark/20 border border-brand/30 rounded-xl flex items-center justify-center flex-shrink-0"
                  >
                    <svg
                      class="w-6 h-6 text-brand"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-white font-bold mb-1">Client Focused</h4>
                    <p class="text-sm text-gray-500">
                      Your satisfaction is our priority
                    </p>
                  </div>
                </div>
              </div>

              <div class="flex gap-4 pt-6">
                <button
                  class="px-8 py-4 bg-gradient-to-r from-brand to-brand-dark rounded-full text-black font-bold hover:shadow-2xl hover:shadow-brand/50 transition-all duration-300"
                >
                  Our Story
                </button>
                <button
                  class="px-8 py-4 border-2 border-brand text-brand rounded-full font-bold hover:bg-brand hover:text-black transition-all duration-300"
                >
                  View Portfolio
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Leadership Section -->
        <div>
          <!-- Section Header -->
          <div class="text-center mb-16">
            <div
              class="inline-flex items-center gap-2 px-4 py-2 bg-brand/10 border border-brand/30 rounded-full mb-6"
            >
              <svg
                class="w-4 h-4 text-brand"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
              <span class="text-sm font-semibold text-brand"
                >Leadership</span
              >
            </div>
            <h2 class="text-5xl lg:text-6xl font-bold mb-6">
              <span class="text-white">Meet The </span>
              <span
                class="text-transparent bg-clip-text bg-gradient-to-r from-accent-yellow-light via-accent-yellow to-accent-yellow-dark"
                >Visionaries</span
              >
            </h2>
            <p class="text-xl text-gray-400 max-w-3xl mx-auto">
              Two exceptional leaders united by a shared commitment to
              excellence and innovation in real estate development.
            </p>
          </div>

          <div class="grid lg:grid-cols-2 gap-6 max-w-3xl mx-auto">
            <!-- CEO Card - Aiben Properties (First) -->
            <div
              class="group relative bg-gradient-to-br from-gray-900 to-black border border-brand/20 rounded-2xl overflow-hidden hover:border-brand/60 transition-all duration-500"
            >
              <!-- Glow -->
              <div
                class="absolute inset-0 bg-gradient-to-br from-brand/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              ></div>

              <!-- Image Section - Reduced Height -->
              <div class="relative h-[400px] overflow-hidden">
                <img
                  src="../assets/images/aiben.webp"
                  class="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  alt="CEO"
                />

                <!-- Gradient -->
                <div
                  class="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"
                ></div>

                <!-- Company Badge -->
                <div
                  class="absolute top-3 left-3 px-3 py-1 bg-brand rounded-full"
                >
                  <span class="text-[10px] font-bold text-black"
                    >Aiben Properties</span
                  >
                </div>

                <!-- Name -->
                <div class="absolute bottom-4 left-4 right-4">
                  <h3 class="text-xl font-bold text-white">
                    Chief Andy Elerewe
                  </h3>
                  <p class="text-brand text-sm font-semibold">
                    Chairman/GMD
                  </p>
                </div>

                <!-- Hover Overlay -->
                <div
                  class="absolute inset-0 bg-black/95 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col justify-end p-5"
                >
                  <div class="space-y-3">
                    <div>
                      <h3 class="text-xl font-bold text-white">
                        Chief Andy Elerewe
                      </h3>
                      <p class="text-brand text-sm font-semibold">
                        Chairman/GMD
                      </p>
                      <p class="text-gray-400 text-xs">Aiben Properties</p>
                    </div>

                    <p class="text-gray-300 text-xs leading-relaxed">
                      An innovative entrepreneur passionate about sustainable
                      communities and modern development.
                    </p>

                    <div class="space-y-1.5">
                      <div
                        class="flex items-center gap-2 text-xs text-gray-300"
                      >
                        <div
                          class="w-1.5 h-1.5 bg-brand rounded-full"
                        ></div>
                        <span>12+ Years Industry Leadership</span>
                      </div>
                      <div
                        class="flex items-center gap-2 text-xs text-gray-300"
                      >
                        <div
                          class="w-1.5 h-1.5 bg-brand rounded-full"
                        ></div>
                        <span>200+ Community Projects</span>
                      </div>
                    </div>

                    <button
                      class="flex items-center gap-2 text-brand font-semibold text-xs"
                    >
                      Read Full Bio
                      <svg
                        class="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- CEO Card - MshelHomes Limited (Second) -->
            <div
              class="group relative bg-gradient-to-br from-gray-900 to-black border border-brand/20 rounded-2xl overflow-hidden hover:border-brand/60 transition-all duration-500"
            >
              <!-- Glow -->
              <div
                class="absolute inset-0 bg-gradient-to-br from-brand/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              ></div>

              <!-- Image Section - Reduced Height -->
              <div class="relative h-[400px] overflow-hidden">
                <img
                  src="../assets/images/barkaimagee.png"
                  class="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                  alt="CEO"
                />

                <!-- Gradient -->
                <div
                  class="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent"
                ></div>

                <!-- Company Badge -->
                <div
                  class="absolute top-3 left-3 px-3 py-1 bg-brand rounded-full"
                >
                  <span class="text-[10px] font-bold text-black"
                    >MshelHomes Limited</span
                  >
                </div>

                <!-- Name -->
                <div class="absolute bottom-4 left-4 right-4">
                  <h3 class="text-xl font-bold text-white">
                    ARC. Dr. Barka Umaru Mshelia
                  </h3>
                  <p class="text-brand text-sm font-semibold">GMD/CEO</p>
                </div>

                <!-- Hover Overlay -->
                <div
                  class="absolute inset-0 bg-black/95 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-all duration-500 flex flex-col justify-end p-5"
                >
                  <div class="space-y-3">
                    <div>
                      <h3 class="text-xl font-bold text-white">
                        ARC. Dr. Barka Umaru Mshelia
                      </h3>
                      <p class="text-brand text-sm font-semibold">
                        GMD/CEO
                      </p>
                      <p class="text-gray-400 text-xs">Mshel Group Limited</p>
                    </div>

                    <p class="text-gray-300 text-xs leading-relaxed">
                      An innovative entrepreneur passionate about sustainable
                      communities and modern development.
                    </p>

                    <div class="space-y-1.5">
                      <div
                        class="flex items-center gap-2 text-xs text-gray-300"
                      >
                        <div
                          class="w-1.5 h-1.5 bg-brand rounded-full"
                        ></div>
                        <span>12+ Years Industry Leadership</span>
                      </div>
                      <div
                        class="flex items-center gap-2 text-xs text-gray-300"
                      >
                        <div
                          class="w-1.5 h-1.5 bg-brand rounded-full"
                        ></div>
                        <span>200+ Community Projects</span>
                      </div>
                    </div>

                    <button
                      class="flex items-center gap-2 text-brand font-semibold text-xs"
                    >
                      Read Full Bio
                      <svg
                        class="w-3.5 h-3.5"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Partnership Quote -->
          <div
            class="mt-16 relative bg-gradient-to-r from-gray-900 via-black to-gray-900 border border-brand/30 rounded-3xl p-12 overflow-hidden"
          >
            <div
              class="absolute top-0 left-0 w-64 h-64 bg-brand/10 rounded-full blur-3xl"
            ></div>
            <div
              class="absolute bottom-0 right-0 w-64 h-64 bg-brand-dark/10 rounded-full blur-3xl"
            ></div>

            <div class="relative text-center max-w-4xl mx-auto">
              <svg
                class="w-16 h-16 text-brand/30 mx-auto mb-6"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z"
                />
              </svg>
              <blockquote
                class="text-2xl lg:text-3xl font-medium text-white leading-relaxed mb-6"
              >
                "Together, we're not just building properties—we're creating
                legacies, shaping communities, and defining the future of
                premium real estate development."
              </blockquote>
              <div class="flex items-center justify-center gap-4">
                <div
                  class="w-12 h-1 bg-gradient-to-r from-transparent via-brand to-transparent"
                ></div>
                <p class="text-brand font-semibold">
                  Joint Vision Statement
                </p>
                <div
                  class="w-12 h-1 bg-gradient-to-r from-transparent via-brand to-transparent"
                ></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Services Section -->
    <section
      id="services"
      class="relative py-24 px-6 overflow-hidden bg-gradient-to-b from-black to-gray-950"
    >
      <!-- Section Header -->
      <div class="max-w-7xl mx-auto">
        <div class="text-center mb-16">
          <div
            class="inline-flex items-center gap-2 px-4 py-2 bg-brand/10 border border-brand/30 rounded-full mb-6"
          >
            <span class="text-sm font-semibold text-brand"
              >Our Services</span
            >
          </div>
          <h2 class="text-5xl lg:text-6xl font-bold mb-6">
            <span class="text-white">Collaborative</span>
            <span
              class="text-transparent bg-clip-text bg-gradient-to-r from-accent-red-light via-accent-red to-accent-red-dark"
            >
              Excellence</span
            >
          </h2>
          <p class="text-xl text-gray-400 max-w-3xl mx-auto">
            Through our strategic partnership, we deliver comprehensive real
            estate solutions that combine innovation, quality, and unmatched
            expertise across all aspects of property development.
          </p>
        </div>

        <!-- Services Grid -->
        <div class="grid md:grid-cols-3 gap-8">
          <!-- Service 1 -->
          <div
            class="group relative bg-gradient-to-br from-gray-900 to-black border border-brand/20 rounded-3xl p-8 hover:border-brand/60 transition-all duration-500 hover:-translate-y-2"
          >
            <div
              class="absolute inset-0 bg-gradient-to-br from-brand/5 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            ></div>

            <div class="relative">
              <div
                class="w-16 h-16 bg-gradient-to-br from-brand to-brand-dark rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300"
              >
                <svg
                  class="w-8 h-8 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
              </div>
              <h3
                class="text-2xl font-bold text-white mb-4 group-hover:text-brand transition-colors"
              >
                Building Construction
              </h3>
              <p class="text-gray-400 leading-relaxed mb-6">
                Designing and constructing durable, high-quality residential and
                commercial buildings that set new standards in modern
                architecture and engineering excellence.
              </p>
              <div
                class="flex items-center text-brand font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
              >
                Discover more
                <svg
                  class="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          </div>

          <!-- Service 2 -->
          <div
            class="group relative bg-gradient-to-br from-gray-900 to-black border border-brand/20 rounded-3xl p-8 hover:border-brand/60 transition-all duration-500 hover:-translate-y-2"
          >
            <div
              class="absolute inset-0 bg-gradient-to-br from-brand/5 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            ></div>

            <div class="relative">
              <div
                class="w-16 h-16 bg-gradient-to-br from-brand to-brand-dark rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300"
              >
                <svg
                  class="w-8 h-8 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"
                  />
                </svg>
              </div>
              <h3
                class="text-2xl font-bold text-white mb-4 group-hover:text-brand transition-colors"
              >
                Civil Engineering
              </h3>
              <p class="text-gray-400 leading-relaxed mb-6">
                Large-scale infrastructure projects including roads, bridges,
                drainage systems, and public works designed to enhance
                connectivity and community development.
              </p>
              <div
                class="flex items-center text-brand font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
              >
                Discover more
                <svg
                  class="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          </div>

          <!-- Service 3 -->
          <div
            class="group relative bg-gradient-to-br from-gray-900 to-black border border-brand/20 rounded-3xl p-8 hover:border-brand/60 transition-all duration-500 hover:-translate-y-2"
          >
            <div
              class="absolute inset-0 bg-gradient-to-br from-brand/5 to-transparent rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            ></div>

            <div class="relative">
              <div
                class="w-16 h-16 bg-gradient-to-br from-brand to-brand-dark rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300"
              >
                <svg
                  class="w-8 h-8 text-black"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
              </div>
              <h3
                class="text-2xl font-bold text-white mb-4 group-hover:text-brand transition-colors"
              >
                Estate Development
              </h3>
              <p class="text-gray-400 leading-relaxed mb-6">
                Creating sustainable, well-planned estates that provide modern
                living experiences, combining aesthetics, comfort, and
                functionality to build thriving communities.
              </p>
              <div
                class="flex items-center text-brand font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
              >
                Discover more
                <svg
                  class="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <!-- Bottom CTA -->
        <div
          class="mt-16 relative bg-gradient-to-r from-gray-900 via-black to-gray-900 border border-brand/30 rounded-3xl p-10 overflow-hidden"
        >
          <div
            class="absolute top-0 right-0 w-64 h-64 bg-brand/10 rounded-full blur-3xl"
          ></div>
          <div
            class="relative flex flex-col lg:flex-row items-center justify-between gap-8"
          >
            <div class="flex-1">
              <h3 class="text-3xl font-bold text-white mb-4">
                Partner with Excellence
              </h3>
              <p class="text-gray-400 text-lg">
                Join forces with two industry leaders committed to integrity,
                innovation, and delivering exceptional results. Our combined
                expertise ensures every project exceeds expectations through
                meticulous planning and execution.
              </p>
            </div>
            <button
              class="px-8 py-4 bg-gradient-to-r from-brand to-brand-dark rounded-full text-black font-bold text-lg hover:shadow-2xl hover:shadow-brand/50 transition-all duration-300 whitespace-nowrap"
            >
              Start Your Project
            </button>
          </div>
        </div>
      </div>
    </section>

    <section id="contact" class="bg-black py-20 px-6">
      <div class="max-w-5xl mx-auto grid lg:grid-cols-2 gap-12">
        <!-- Left Text Block -->
        <div class="space-y-6">
          <h2 class="text-4xl font-bold text-white leading-tight">
            Get in Touch
          </h2>
          <p class="text-gray-400 text-lg">
            Have questions, partnership ideas, or need support? We'd love to
            hear from you. Reach out and our team will get back to you shortly.
          </p>

          <!-- Contact Info -->
          <div class="space-y-4">
            <div class="flex items-start gap-4">
              <div
                class="w-10 h-10 flex items-center justify-center rounded-lg bg-brand/20 text-brand"
              >
                <svg
                  class="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 8l7.89 5.26c.67.45 1.54.45 2.21 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <p class="text-white font-semibold">Email</p>
                <p class="text-gray-400">info@hutuabuja.com</p>
              </div>
            </div>

            <div class="flex items-start gap-4">
              <div
                class="w-10 h-10 flex items-center justify-center rounded-lg bg-brand/20 text-brand"
              >
                <svg
                  class="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 5h2l3.6 7.59-1.35 2.45A1 1 0 008 17h12v-2H9.42a.25.25 0 01-.22-.37L10.1 13h7.45a1 1 0 00.92-.62L21 6H6"
                  />
                </svg>
              </div>
              <div>
                <p class="text-white font-semibold">Phone</p>
                <p class="text-gray-400">+234 000 000 0000</p>
              </div>
            </div>

            <div class="flex items-start gap-4">
              <div
                class="w-10 h-10 flex items-center justify-center rounded-lg bg-brand/20 text-brand"
              >
                <svg
                  class="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17.657 16.657L13 21.314l-4.657-4.657A8 8 0 1117.657 16.657z"
                  />
                </svg>
              </div>
              <div>
                <p class="text-white font-semibold">Office Address</p>
                <p class="text-gray-400">
                  Hutu Abuja, close to Centenary City, Airport Road
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Contact Form -->
        <form
          class="bg-gray-900/40 border border-brand/20 rounded-2xl p-8 space-y-6 backdrop-blur"
        >
          <div>
            <label class="text-gray-300 text-sm mb-1 block">Full Name</label>
            <input
              type="text"
              class="w-full p-3 bg-black/50 border border-gray-700 rounded-lg text-white focus:border-brand focus:outline-none"
            />
          </div>

          <div>
            <label class="text-gray-300 text-sm mb-1 block"
              >Email Address</label
            >
            <input
              type="email"
              class="w-full p-3 bg-black/50 border border-gray-700 rounded-lg text-white focus:border-brand focus:outline-none"
            />
          </div>

          <div>
            <label class="text-gray-300 text-sm mb-1 block">Message</label>
            <textarea
              rows="4"
              class="w-full p-3 bg-black/50 border border-gray-700 rounded-lg text-white focus:border-brand focus:outline-none"
            ></textarea>
          </div>

          <button
            class="w-full py-3 bg-brand text-black font-semibold rounded-lg hover:bg-brand-light transition"
          >
            Send Message
          </button>
        </form>
      </div>
    </section>
  </div>
</template>

<script>
import NavBar from "@/layout/NavBar.vue";

export default {
  name: "HomePage",
  components: {
    NavBar,
  },
  data() {
    return {
      currentSlide: 0,
      slideInterval: null,
      slides: [
        {
          image: require("@/assets/images/penthouse.jpg"),
          alt: "Luxury Penthouse",
          title: "Premium Quality",
          subtitle: "Award-Winning Developments",
        },
        {
          image: require("@/assets/images/horse-ride.jpg"),
          alt: "Modern luxury home",
          title: "Innovation & Design",
          subtitle: "Cutting-Edge Architecture",
        },
        {
          image:
            "https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=800&h=1000&fit=crop",
          alt: "Contemporary architecture",
          title: "Excellence Delivered",
          subtitle: "Trusted by Thousands",
        },
        {
          image: require("@/assets/images/aiben1.jpeg"),
          alt: "Luxury estate",
          title: "Dream Homes",
          subtitle: "Where Luxury Meets Comfort",
        },
      ],
    };
  },
  mounted() {
    this.startSlideshow();
  },
  beforeUnmount() {
    this.stopSlideshow();
  },
  methods: {
    startSlideshow() {
      this.slideInterval = setInterval(() => {
        this.nextSlide();
      }, 5000); // Change slide every 5 seconds
    },
    stopSlideshow() {
      if (this.slideInterval) {
        clearInterval(this.slideInterval);
      }
    },
    nextSlide() {
      this.currentSlide = (this.currentSlide + 1) % this.slides.length;
    },
    prevSlide() {
      this.currentSlide =
        this.currentSlide === 0
          ? this.slides.length - 1
          : this.currentSlide - 1;
    },
  },
};
</script>

<style scoped>
/* Grid Pattern */
.grid-pattern {
  background-image: linear-gradient(rgba(255, 215, 0, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 215, 0, 0.1) 1px, transparent 1px);
  background-size: 50px 50px;
}

/* Animation Delay */
.animation-delay-1000 {
  animation-delay: 1s;
}

/* Pulse Animation */
@keyframes pulse {
  0%,
  100% {
    opacity: 0.1;
  }
  50% {
    opacity: 0.2;
  }
}

.animate-pulse {
  animation: pulse 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

/* Bounce Animation */
@keyframes bounce {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

.animate-bounce {
  animation: bounce 2s infinite;
}

/* Fade Transition for Slider */
.fade-enter-active {
  transition: opacity 1s ease;
}

.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from {
  opacity: 0;
}

.fade-leave-to {
  opacity: 0;
}
</style>
